import { CommonModule } from "@angular/common";
import { Component, effect, inject, LOCALE_ID, ViewChild } from "@angular/core";
import { ActivatedRoute, NavigationStart, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngrx/store";
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Menubar, MenubarModule } from "primeng/menubar";
import { combineLatest, first, Observable, of, shareReplay } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { AuthSelectors } from "src/app/modules/auth/ngrx/auth.selectors.types";
import { AppSelectors } from "src/app/ngrx/app.selectors.types";
import { AppState } from "src/app/ngrx/root.reducer";
import { APP_ROLES } from "src/app/modules/auth/shared/models/auth-roles";
import { GuideDownloadService } from "@ripsim/guide-download";
import { CopyrightComponent } from "src/app/shared/components/copyright/copyright.component";
import { OTA_KEY_DERIVATION_SCREEN_VISIBLE } from "src/app/shared/constants/feature-flags.constants";
import { environment } from "src/environments/environment";
import { AuthService } from "../../modules/auth/shared/services/auth.service";

const GUIDE_BUSY_ICON = "pi pi-fw pi-spin pi-spinner";

/**
 * Help Guides label per document stem the API may list. A stem without an entry is hidden from the
 * submenu and reported on the console; showing it takes a `@@guide.<Stem>` entry here. The label
 * also names the file when a popup blocker forces a download.
 */
const GUIDE_LABELS: Readonly<Record<string, string>> = {
  QuickStartGuide: $localize`:@@guide.QuickStartGuide:User Manual`,
  GettingStartedGuide: $localize`:@@guide.GettingStartedGuide:Getting Started Guide`
};

@UntilDestroy()
@Component({
  selector: "rs-header-component",
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
  standalone: true,
  imports: [CommonModule, ButtonModule, MenubarModule, CopyrightComponent]
})
export class HeaderComponent {
  @ViewChild(Menubar) menubar: Menubar;

  private readonly localeId = inject(LOCALE_ID);

  private readonly otaKeyDerivationScreenVisible = inject(OTA_KEY_DERIVATION_SCREEN_VISIBLE);

  /**
   * Help document items by stem, held so the state of an in-flight request can be shown on the item
   * that started it.
   */
  private readonly guideItems = new Map<string, MenuItem>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private guideDownloadService: GuideDownloadService
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        untilDestroyed(this)
      )
      .subscribe(() => this.hideMenu());

    /**
     * Reflects an in-flight document request on its menu item, so a click during the wait is visibly
     * refused rather than dropped. The service owns the request, the toasts, and the guard that makes
     * the refused click a no-op. `openingStems` is read up front so the effect tracks it before any
     * item exists.
     */
    effect(() => {
      this.guideDownloadService.openingStems();
      for (const [stem, item] of this.guideItems) {
        const opening = this.guideDownloadService.isOpening(stem);
        item.disabled = opening;
        item.icon = opening ? GUIDE_BUSY_ICON : undefined;
      }
    });
  }

  readonly logos = environment.logos;

  readonly pageName$: Observable<string> = this.store.select(AppSelectors.getPageName);

  /**
   * Waits for the first non-empty roles emission and the document list together. A failed list call
   * leaves the Help menu without its Help Guides submenu and does not block the rest of the menu.
   *
   * <remarks>
   * Shared, because building the menu records each document's item in `guideItems` for the
   * busy-state effect to reach. A second subscriber to a cold pipeline would rebuild the menu and
   * leave that map pointing at items no template renders.
   * </remarks>
   */
  readonly menuItems$: Observable<MenuItem[]> = combineLatest([
    this.store.select(AuthSelectors.roles).pipe(first((roles) => roles.length > 0)),
    this.guideDownloadService.list().pipe(
      catchError((error: unknown) => {
        console.error("Help documents could not be listed", error);
        return of([] as string[]);
      })
    )
  ]).pipe(
    map(([roles, stems]) => this.generateNavigationMenu(roles, stems)),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  private generateNavigationMenu(roles: string[], stems: string[]): MenuItem[] {
    const items: MenuItem[] = [];
    if (roles.includes(APP_ROLES.ewiz)) {
      items.push({
        label: $localize`Design Studio`,
        icon: "pi pi-fw pi-mobile",
        items: [
          {
            label: $localize`Profile Management`,
            // When environment.linkToReact is enabled, hand off to the migrated
            // React screen at /web; otherwise stay in the Angular app (default).
            ...(environment.linkToReact
              ? { url: `/web/${this.localeId}/esim/profiles` }
              : { command: () => this.router.navigate(["/esim/profiles"]) })
          },
          {
            label: $localize`Applet Warehouse`,
            command: () => this.router.navigate(["/applets"])
          }
        ]
      });
    }
    const edgarMenuItems: MenuItem[] = [];
    if (roles.includes(APP_ROLES.edgar)) {
      edgarMenuItems.push(
        {
          label: $localize`Job Templates`,
          command: () => this.router.navigate(["/edgar/manage"])
        },
        {
          label: $localize`Input Files`,
          command: () => this.router.navigate(["/edgar/files"])
        },
        {
          label: $localize`Orders`,
          command: () => this.router.navigate(["/edgar/jobs"])
        }
      );
    }
    if (roles.includes(APP_ROLES.profileViewer)) {
      edgarMenuItems.push({
        label: $localize`eSIM Viewer`,
        command: () =>
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { iccidLookup: true },
            queryParamsHandling: "merge"
          })
      });
    }
    if (edgarMenuItems.length > 0) {
      items.push({
        label: $localize`e.D.G.A.R.`,
        icon: "pi pi-fw pi-sliders-h",
        items: edgarMenuItems
      });
    }

    if (roles.includes(APP_ROLES.hsm)) {
      items.push({
        label: $localize`Key Vault`,
        icon: "pi pi-fw pi-briefcase",
        items: [
          {
            label: $localize`Milenage Parameters`,
            command: () => this.router.navigate(["/securedata/milenage"])
          },
          {
            label: $localize`TUAK Parameters`,
            command: () => this.router.navigate(["/securedata/tuak"])
          },
          {
            label: $localize`HNPK`,
            command: () => this.router.navigate(["/securedata/hnpk"])
          },
          {
            label: $localize`Transport Keys`,
            command: () => this.router.navigate(["/securedata/transportkeys"])
          },
          ...(this.otaKeyDerivationScreenVisible
            ? [
                {
                  label: $localize`Key Derivation`,
                  command: () => this.router.navigate(["/securedata/derivation"])
                }
              ]
            : []),
          {
            label: $localize`PGP Keys`,
            command: () => this.router.navigate(["/securedata/pgpkeys"])
          }
        ]
      });
      const settingsItems: MenuItem[] = [
        {
          label: $localize`eSIM Destinations`,
          command: () => this.router.navigate(["/settings/destination"])
        },
        {
          label: $localize`Output File Destinations`,
          command: () => this.router.navigate(["/settings/output-file"])
        },
        {
          label: $localize`Input Configurator`,
          command: () => this.router.navigate(["/settings/input-configurator"])
        },
        {
          label: $localize`Output Configurator`,
          items: [
            {
              label: $localize`Output File Configurator`,
              command: () => this.router.navigate(["/settings/output-configurator/output-file-definitions"])
            },
            {
              label: $localize`Output Package Configurator`,
              command: () => this.router.navigate(["/settings/output-configurator/output-packages"])
            }
          ]
        }
      ];

      if (roles.includes(APP_ROLES.intelligent)) {
        settingsItems.push({
          label: $localize`Intelligent eSIM Configurator`,
          items: [
            {
              label: $localize`Intelligent Variable Specification Management`,
              command: () => this.router.navigate(["/settings/intelligent-variable-spec"])
            },
            {
              label: $localize`Intelligent ASN.1 Template Management`,
              command: () => this.router.navigate(["/settings/intelligent-asn-template"])
            }
          ]
        });
      }

      items.push({
        label: $localize`Settings`,
        icon: "pi pi-fw pi-cog",
        items: settingsItems
      });
    }

    const documentItems: MenuItem[] = stems.flatMap((stem) => {
      const label: string | undefined = GUIDE_LABELS[stem];
      if (label === undefined) {
        console.error(`No Help label for document '${stem}'; add $localize id @@guide.${stem}`);
        return [];
      }
      const item: MenuItem = {
        label,
        command: () => {
          this.hideMenu();
          this.guideDownloadService.open(stem, label);
        }
      };
      this.guideItems.set(stem, item);
      return [item];
    });

    items.push({
      label: $localize`Help`,
      icon: "pi pi-fw pi-question-circle",
      items: [
        ...(documentItems.length > 0
          ? [
              {
                label: $localize`:@@helpGuidesMenuItem:Help Guides`,
                items: documentItems
              }
            ]
          : []),
        {
          label: $localize`Email Support`,
          command: () => {
            this.hideMenu();
            const emailAddress = $localize`${environment.emailAddress}`;
            const emailSubject = $localize`RiPSIM Customer Support Request`;
            window.location.href = `mailto:${emailAddress}?subject=${emailSubject}`;
          }
        },
        {
          label: $localize`Support Portal`,
          url: $localize`${environment.supportPortal}`,
          target: "_blank"
        },
        {
          label: $localize`About`,
          command: () =>
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { showAbout: true },
              queryParamsHandling: "merge"
            })
        }
      ]
    });

    return items;
  }

  activateHover(): void {
    if (this.menubar) {
      this.menubar.dirty = true;
    }
  }

  hideMenu(): void {
    this.menubar?.hide();

    const { activeElement } = document;
    if (activeElement) {
      if (this.menubar?.el?.nativeElement?.contains(activeElement)) {
        (activeElement as HTMLElement)?.blur();
      }
    }
  }

  onLogout(): void {
    this.authService.logout();
  }
}
